import React from 'react';
import PopoverContainer from 'components/pure/utils/PopoverContainer';
import isNil from 'lodash/isNil';
import {select} from 'd3-selection';
import {interpolate} from 'd3-interpolate';
import {transition} from 'd3-transition';
import Theme from 'styles/theme';
import {arc as d3arc, pie as d3pie} from 'd3-shape';
import {easeLinear} from 'd3-ease';
import round from 'utils/round';
import classNames from 'classnames';
import './DonutChart.styl';

// Something to render when there is no data
const emptyData = [{value: 1}];

export default class DonutChart extends React.PureComponent {
  getRadius() {
    return Math.min(this.props.width, this.props.height) / 2;
  }
  componentDidMount() {
    const svg = select(this.svg);

    const centerX = this.props.width / 2;
    const centerY = this.props.height - this.getRadius();
    const transform = `translate(${centerX}, ${centerY})`;

    svg.append('g').attr('transform', transform).attr('class', 'pie');

    const labelStemG = select(this.svg)
      .append('g')
      .attr('transform', transform)
      .attr('class', 'label-stem');

    labelStemG.append('line').attr('class', 'label-arrow-horizontal');
    labelStemG.append('line').attr('class', 'label-arrow-vertical');

    this.renderData();
  }

  componentDidUpdate() {
    this.renderData();
  }

  renderData() {
    const radius = Math.min(this.props.width, this.props.height) / 2;
    const data = this.props.data.filter(d => d.value > 0).toArray();

    // These two properties can be used to manually adjust the position of the anchor giving it an X,Y offset from the center of the arc
    const {anchorOffsetX = 0, anchorOffsetY = 0} = this.props;
    const outerRadius = this.props.label ? 30 : 0;
    const thickness = this.props.thickness || 150;

    const arc = d3arc()
      .outerRadius(radius - outerRadius)
      .innerRadius(radius - outerRadius - thickness);

    const {padAngle, sorted = true} = this.props;
    const pie = d3pie()
      .padAngle(!isNil(padAngle) ? padAngle : Math.PI / 80)
      .sort((a, b) => (sorted ? b.value - a.value : 0))
      .value(d => d.value);

    const t = transition().duration(1000).ease(easeLinear);

    const total = data.reduce((memo, d) => memo + d.value, 0);
    const pieData = pie(total > 0 ? data : emptyData);

    const svg = select(this.svg);
    const selection = svg
      .select('.pie')
      .selectAll('.arc')
      .data(pieData, d => d.data.id);

    const {popoverContainer} = this;
    const self = this;
    const enterSelection = selection.enter().append('g').attr('class', 'arc');

    enterSelection
      .append('path')
      .attr('class', 'donut-segment')
      .attr('d', arc)
      .style('fill', d => (d.data.selected ? this.props.color : Theme.palette.dustyGray));

    enterSelection
      .append('circle')
      .attr('class', 'tooltip-anchor')
      .attr('cx', d => arc.centroid(d)[0] + anchorOffsetX)
      .attr('cy', d => arc.centroid(d)[1] + anchorOffsetY);

    enterSelection.each(function (d) {
      self.props.tooltipContent &&
        total > 0 &&
        popoverContainer.registerPopoverItem(
          this.querySelector('.donut-segment'),
          () => self.props.tooltipContent(select(this).datum().data),
          this.querySelector('.tooltip-anchor')
        );
    });

    function arcTween(a) {
      let i = interpolate(this._current, a);
      this._current = i(0);
      return function (t) {
        return arc(i(t));
      };
    }

    selection
      .each(function (d) {
        select(this)
          .select('.tooltip-anchor')
          .attr('cx', d => arc.centroid(d)[0] + anchorOffsetX)
          .attr('cy', d => arc.centroid(d)[1] + anchorOffsetY);
      })
      .select('path')
      .transition(t)
      .attrTween('d', arcTween)
      .style('fill', d => (d.data.selected ? this.props.color : Theme.palette.dustyGray));

    selection.exit().remove();

    const selected = pieData.find(d => d.data.selected);
    if (selected && this.props.label) {
      const [x, y] = arc.centroid(selected);
      const rightEdgeX = this.props.width / 2 - 70;

      svg
        .select('.label-arrow-horizontal')
        .transition(t)
        .style('opacity', 1)
        // .attrTween('d', arcTween);
        .attr('x1', x)
        .attr('y1', y)
        .attr('x2', rightEdgeX)
        .attr('y2', y);

      svg
        .select('.label-arrow-vertical')
        .transition(t)
        .style('opacity', 1)
        // .attrTween('d', arcTween);
        .attr('x1', rightEdgeX)
        .attr('y1', y)
        .attr('x2', rightEdgeX)
        .attr('y2', -this.props.height + this.getRadius() + 100);
    } else {
      svg.select('.label-arrow-horizontal').style('opacity', 0);
      svg.select('.label-arrow-vertical').style('opacity', 0);
    }
  }

  render() {
    const total = this.props.data.reduce((memo, d) => memo + d.value, 0);
    const {height, width} = this.props;
    const selected = this.props.data.find(d => d.selected);
    const showLabel =
      this.props.forceShowLabel || (selected && selected.value > 0 && this.props.label);

    return (
      <div className={classNames('donut-chart', this.props.className)}>
        <PopoverContainer
          anchorOriginVertical="center"
          targetOriginVertical="center"
          ref={r => (this.popoverContainer = r)}
        />
        <div className="svg-container">
          <svg
            ref={ref => (this.svg = ref)}
            preserveAspectRatio="xMinYMin meet"
            className="svg-content-responsive"
            viewBox={`0 0 ${width} ${height}`}
          />
        </div>
        {showLabel && (
          <div className="label">
            {this.props.label(selected ? round((selected.value / total) * 100) : 0)}
          </div>
        )}
      </div>
    );
  }
}
